<template>
    <div class="workforce-history-content">
        <div class="workforce-history__filters">
            <v-select ref="employeeSelect" v-model="selectedEmployees" class="workforce-history__employee-filter"
                      :options="employeeOptions" label="full_name" multiple :filterable="false"
                      :get-option-key="option => option.employee_id"
                      append-to-body :calculate-position="positionDropdown"
                      :close-on-select="false" :loading="employeesLoading" placeholder="Все сотрудники"
                      aria-label="Фильтр истории по сотрудникам"
                      @open="openEmployeePicker" @close="closeEmployeePicker" @search="searchEmployees">
                <template #no-options>
                    {{ employeesError || (employeesLoading ? 'Загружаем…' : 'Сотрудники не найдены') }}
                    <button v-if="employeesError" type="button" class="btn btn-sm btn-link"
                            @mousedown.prevent @click.stop="loadEmployees(true)">Повторить</button>
                </template>
                <template #list-footer>
                    <li v-if="employeesHasMore || employeesError" class="workforce-history__picker-more">
                        <button type="button" class="btn btn-sm btn-link" :disabled="employeesLoading"
                                @mousedown.prevent @click.stop="loadEmployees(false)">
                            {{ employeesError ? 'Повторить загрузку' : 'Показать ещё сотрудников' }}
                        </button>
                    </li>
                </template>
            </v-select>
            <b-form-input ref="historySearch" v-model.trim="filters.query" type="search" size="sm"
                          placeholder="Действие, сотрудник или автор" aria-label="Поиск по истории" />
            <b-form-select v-model="filters.objectId" :options="historyObjectOptions" size="sm"
                           aria-label="Фильтр истории по объекту" />
            <div class="workforce-history__filter-actions">
                <small>Общая история доски и плана за всё время</small>
                <b-button size="sm" variant="link" :disabled="loading" @click="reload">Обновить</b-button>
                <b-button v-if="hasFilters" size="sm" variant="link" @click="clearFilters">Сбросить фильтры</b-button>
            </div>
        </div>
        <div class="workforce-history__body" :aria-busy="loading || loadingMore ? 'true' : 'false'">
            <b-alert v-if="errorMessage" show variant="danger">
                {{ errorMessage }}
                <b-button size="sm" variant="outline-danger" @click="load(failedReset)">Повторить</b-button>
            </b-alert>
            <div v-if="loading" class="workforce-history__loading" role="status">
                <b-spinner small variant="primary" /> <span>Загружаем историю…</span>
            </div>
            <div v-if="!events.length && !loading && !errorMessage" class="workforce-history__empty">
                <i class="far fa-clock" aria-hidden="true"></i>
                <strong>Изменений не найдено</strong>
                <span>Попробуйте изменить фильтры.</span>
            </div>
            <div class="workforce-history__list">
                <article v-for="event in events" :key="event.id" class="workforce-history__event">
                    <div class="workforce-history__event-top">
                        <strong>{{ eventTitle(event) }}</strong>
                        <time :datetime="historyTimestamp(event)">{{ formatDateTime(historyTimestamp(event)) }}</time>
                    </div>
                    <button type="button" class="workforce-history__employee" @click="$emit('open-employee', event.employee)">
                        <i class="fas fa-user" aria-hidden="true"></i> {{ employeeName(event) }}
                    </button>
                    <p v-if="eventDescription(event)">{{ eventDescription(event) }}</p>
                    <div v-if="event.target_names && event.target_names.length" class="workforce-history__targets">
                        <span v-for="target in event.target_names" :key="target">{{ target }}</span>
                    </div>
                    <div class="workforce-history__meta">
                        <span><i class="fas fa-user-edit" aria-hidden="true"></i> {{ actorName(event) }}</span>
                        <span>{{ sourceLabel(event.source) }}</span>
                    </div>
                </article>
            </div>
            <b-button v-if="hasMore && !loading" block size="sm" variant="outline-primary" class="mt-3"
                      :disabled="loadingMore" @click="load(false)">
                <b-spinner v-if="loadingMore" small class="mr-1" /> Показать ещё
            </b-button>
        </div>
    </div>
</template>

<script>
import api from '@/utils/api';
import {socket} from '@/plugins/socket';
import {positionVueSelectDropdown} from '@/utils/vueSelectPositioning';
import {workforceHistoryDescription} from '@/utils/employeeWorkforceHistory';
import {employeeHistoryActorLabel, employeeHistoryEventLabel, employeeHistoryTimestamp,
    employeeSourceLabel, formatEmployeeHistoryDateTime} from './employeeHistoryPresentation';

export default {
    name: 'EmployeeWorkforceHistoryContent',
    props: {
        active: {type: Boolean, default: false},
        objectOptions: {type: Array, default: () => []},
        refreshToken: {type: Number, default: 0}
    },
    data() {
        return {
            filters: {query: '', objectId: ''}, selectedEmployees: [],
            events: [], cursor: null, hasMore: false, loading: false, loadingMore: false,
            errorMessage: '', failedReset: true, requestId: 0, reloadTimer: null,
            serverObjectOptions: null,
            employeeChoices: [], employeeQuery: '', employeesCursor: null, employeesHasMore: false,
            employeesLoading: false, employeesError: '', employeeRequestId: 0, employeeTimer: null,
            employeePickerOpen: false
        };
    },
    computed: {
        historyObjectOptions() {
            return [{value: '', text: 'Все доступные объекты'}].concat(this.serverObjectOptions || this.objectOptions);
        },
        employeeOptions() {
            const selected = new Set(this.selectedEmployees.map(row => Number(row.employee_id)));
            return this.selectedEmployees.concat(this.employeeChoices.filter(row => !selected.has(Number(row.employee_id))));
        },
        employeeIds() { return this.selectedEmployees.map(row => row.employee_id).join(','); },
        hasFilters() { return Boolean(this.employeeIds || this.filters.query || this.filters.objectId); }
    },
    watch: {
        active: {immediate: true, handler(value) {
            if (value) {
                this.reload();
                this.$nextTick(() => {
                    if (this.$refs.historySearch) this.$refs.historySearch.focus();
                });
            }
            else this.pause();
        }},
        'filters.query'() { this.filterChanged(350); },
        'filters.objectId'() {
            this.closeEmployeePicker();
            this.employeeChoices = [];
            this.filterChanged();
        },
        employeeIds() { this.filterChanged(); },
        refreshToken() { this.scheduleReload(250); }
    },
    created() {
        socket.on('workforce:changed', this.onRealtimeChange);
        socket.on('connect', this.onRealtimeChange);
    },
    beforeDestroy() {
        this.pause();
        socket.off('workforce:changed', this.onRealtimeChange);
        socket.off('connect', this.onRealtimeChange);
    },
    methods: {
        pause() {
            this.requestId += 1;
            if (this.reloadTimer) window.clearTimeout(this.reloadTimer);
            this.reloadTimer = null;
            this.loading = false;
            this.loadingMore = false;
            this.closeEmployeePicker();
        },
        onRealtimeChange() { this.scheduleReload(250); },
        scheduleReload(delay = 0) {
            if (!this.active) return;
            this.requestId += 1;
            if (this.reloadTimer) window.clearTimeout(this.reloadTimer);
            this.reloadTimer = window.setTimeout(() => {
                this.reloadTimer = null;
                this.reload();
            }, delay);
        },
        filterChanged(delay = 0) {
            this.events = [];
            this.cursor = null;
            this.hasMore = false;
            this.errorMessage = '';
            this.scheduleReload(delay);
        },
        clearFilters() {
            this.selectedEmployees = [];
            this.filters = {query: '', objectId: ''};
        },
        reload() { return this.load(true); },
        async load(reset) {
            if (!this.active || (!reset && (this.loading || this.loadingMore))) return;
            const requestId = ++this.requestId;
            this.loading = reset;
            this.loadingMore = !reset;
            this.errorMessage = '';
            this.failedReset = reset;
            try {
                const response = await api.get('/api/employees/workforce/history', {params: {
                    q: this.filters.query || undefined, object_id: this.filters.objectId || undefined,
                    employee_ids: this.employeeIds || undefined, cursor: reset ? undefined : this.cursor, limit: 50
                }, ignoreModal: true});
                if (requestId !== this.requestId || !this.active) return;
                const data = response.data || {};
                const loaded = Array.isArray(data.events) ? data.events : [];
                const existing = new Set((reset ? [] : this.events).map(event => String(event.id)));
                this.events = (reset ? [] : this.events).concat(loaded.filter(event => !existing.has(String(event.id))));
                this.cursor = data.next_cursor || null;
                this.hasMore = data.has_more === true && Boolean(this.cursor);
                if (Array.isArray(data.object_options)) this.serverObjectOptions = data.object_options;
            } catch (error) {
                if (requestId !== this.requestId || !this.active) return;
                const data = error && error.response && error.response.data;
                this.errorMessage = data && (data.msg || data.message) || 'Не удалось загрузить историю изменений.';
            } finally {
                if (requestId === this.requestId) { this.loading = false; this.loadingMore = false; }
            }
        },
        openEmployeePicker() {
            this.employeePickerOpen = true;
            this.loadEmployees(true);
        },
        closeEmployeePicker() {
            this.employeePickerOpen = false;
            if (this.$refs.employeeSelect) this.$refs.employeeSelect.open = false;
            this.employeeRequestId += 1;
            if (this.employeeTimer) window.clearTimeout(this.employeeTimer);
            this.employeeTimer = null;
            this.employeesLoading = false;
        },
        searchEmployees(query) {
            if (this.employeeQuery === query) return;
            this.employeeQuery = query;
            this.employeeRequestId += 1;
            this.employeeChoices = [];
            this.employeesHasMore = false;
            if (this.employeeTimer) window.clearTimeout(this.employeeTimer);
            if (!this.active || !this.employeePickerOpen) return;
            this.employeeTimer = window.setTimeout(() => {
                this.employeeTimer = null;
                this.loadEmployees(true);
            }, 350);
        },
        async loadEmployees(reset) {
            if (!this.active || !this.employeePickerOpen || (!reset && this.employeesLoading)) return;
            const requestId = ++this.employeeRequestId;
            this.employeesLoading = true;
            this.employeesError = '';
            if (reset) { this.employeesCursor = null; this.employeesHasMore = false; }
            try {
                const response = await api.get('/api/employees/workforce/history/employees', {params: {
                    q: this.employeeQuery || undefined, object_id: this.filters.objectId || undefined,
                    cursor: reset ? undefined : this.employeesCursor, limit: 20
                }, ignoreModal: true});
                if (requestId !== this.employeeRequestId || !this.active || !this.employeePickerOpen) return;
                const data = response.data || {};
                const old = reset ? [] : this.employeeChoices;
                const ids = new Set(old.map(row => Number(row.employee_id)));
                this.employeeChoices = old.concat((data.employees || []).filter(row => !ids.has(Number(row.employee_id)))
                    .map(row => ({...row, full_name: row.full_name || row.display_name || `Сотрудник #${row.employee_id}`})));
                this.employeesCursor = data.next_cursor || null;
                this.employeesHasMore = data.has_more === true && Boolean(this.employeesCursor);
            } catch (error) {
                if (requestId !== this.employeeRequestId) return;
                this.employeesError = 'Не удалось загрузить сотрудников';
            } finally {
                if (requestId === this.employeeRequestId) this.employeesLoading = false;
            }
        },
        eventTitle: employeeHistoryEventLabel,
        positionDropdown(dropdown, component, position) {
            positionVueSelectDropdown(dropdown, component, position);
            dropdown.style.zIndex = '1100';
        },
        historyTimestamp: employeeHistoryTimestamp,
        actorName: employeeHistoryActorLabel,
        eventDescription: workforceHistoryDescription,
        formatDateTime: formatEmployeeHistoryDateTime,
        sourceLabel: employeeSourceLabel,
        employeeName(event) {
            return (event.employee || {}).display_name || (event.employee || {}).full_name || `Сотрудник #${event.employee_id}`;
        }
    }
};
</script>

<style scoped>
.workforce-history__filters {
    display: grid;
    flex: 0 0 auto;
    grid-template-columns: minmax(0, 1.25fr) minmax(190px, 0.75fr);
    gap: 8px;
    padding: 12px 22px;
    border-bottom: 1px solid #e8edf2;
    background: #f8fafc;
}

.workforce-history__body {
    flex: 1 1 0;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 16px 22px calc(24px + env(safe-area-inset-bottom));
    overscroll-behavior-y: contain;
    scrollbar-gutter: stable;
}

.workforce-history__state,
.workforce-history__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 180px;
    color: #718090;
}

.workforce-history__state {
    gap: 8px;
}

.workforce-history__empty {
    flex-direction: column;
    gap: 6px;
    text-align: center;
}

.workforce-history__empty i {
    margin-bottom: 4px;
    color: #9aabba;
    font-size: 1.8rem;
}

.workforce-history__empty span {
    font-size: 0.8rem;
}

.workforce-history__list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.workforce-history__event {
    padding: 12px 13px;
    border: 1px solid #e0e7ee;
    border-left: 3px solid #6e9bc4;
    border-radius: 0 9px 9px 0;
    background: #fbfcfd;
}

.workforce-history__event-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
}

.workforce-history__event-top strong {
    color: #2e4357;
    font-size: 0.86rem;
}

.workforce-history__event-top time {
    flex-shrink: 0;
    color: #8795a3;
    font-size: 0.68rem;
}

.workforce-history__employee {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin: 6px 0 0;
    padding: 0;
    border: 0;
    background: transparent;
    color: #1769aa;
    font-size: 0.8rem;
    font-weight: 600;
    text-align: left;
}

.workforce-history__event p {
    margin: 7px 0 0;
    color: #536577;
    font-size: 0.78rem;
    white-space: pre-wrap;
}

.workforce-history__targets {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 8px;
}

.workforce-history__targets span {
    padding: 3px 7px;
    border-radius: 999px;
    background: #eaf2f9;
    color: #426987;
    font-size: 0.68rem;
}

.workforce-history__meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 9px;
    color: #8795a3;
    font-size: 0.68rem;
}


.workforce-history-content { display: flex; flex: 1 1 auto; flex-direction: column; min-height: 0; }
.workforce-history__employee-filter, .workforce-history__filter-actions { grid-column: 1 / -1; min-width: 0; }
.workforce-history__filter-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; color: #718090; }
.workforce-history__filter-actions small { margin-right: auto; }
.workforce-history__filter-actions .btn { padding: 0; font-size: .75rem; }
.workforce-history__picker-more { text-align: center; list-style: none; }
.workforce-history__loading { display: flex; justify-content: center; align-items: center; gap: 8px; padding: 10px; color: #718090; }
@media (max-width: 575.98px) {
    .workforce-history__filters { grid-template-columns: 1fr; padding: 10px 15px; }
    .workforce-history__body { padding: 13px 15px calc(20px + env(safe-area-inset-bottom)); }
    .workforce-history__event-top { flex-direction: column; gap: 2px; }
}
</style>
